"use strict";
/**
 * COSTANTI E SOGLIE DI DOMINIO — FONTE UNICA (@leopart/calc).
 *
 * Regola: nessuna app (backend, frontend web, mobile) dichiara più queste
 * soglie in locale. Il censimento 2026-07-26 (vault/Architettura/Calcoli e
 * formule.md) ha trovato: 4 set di soglie RI, 4 banding ACWR e 3 costanti
 * lb→kg diverse nella stessa piattaforma — un RI=72 era "ottimo" per il
 * backend e arancione in metà delle pagine. Mai più.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CM_TO_IN = exports.IN_TO_CM = exports.KG_TO_LB = exports.LB_TO_KG = exports.DELTA_ALERT_PCT = exports.DELTA_WARN_PCT = exports.MONOTONY_WARN_THRESHOLD = exports.DURATION_MAX_MINUTES = exports.DURATION_MIN_MINUTES = exports.RPE_MAX = exports.RPE_MIN = exports.SRPE_BINS = exports.RI_THRESHOLDS = exports.PERCENTILE_BANDS = exports.QUESTIONNAIRE_COVERAGE_BANDS = exports.INJURY_RISK = exports.REPORT_RI_CRITICAL = exports.GPS_COVERAGE = exports.RECOVERY_CMJ_MRSI_BLEND = exports.MONOTONY_OK_THRESHOLD = exports.AFI_ALERT_WEEKS = exports.AFI_ALERT_SCORE = exports.AFI_THRESHOLDS = exports.ACWR_BELL_OPTIMAL = exports.ACWR_BANDS = exports.ACWR_MIN_HISTORY_DAYS = exports.ACWR_CHRONIC_DAYS = exports.ACWR_ACUTE_DAYS = exports.ACWR_ZONES = void 0;
exports.classifyAcwr = classifyAcwr;
exports.acwrOutOfOptimalRange = acwrOutOfOptimalRange;
exports.classifyAcwr5 = classifyAcwr5;
exports.classifyAFI = classifyAFI;
exports.classifyCoverage = classifyCoverage;
exports.classifyPercentile = classifyPercentile;
exports.classifyRI = classifyRI;
exports.srpeBin = srpeBin;
exports.classifyMonotony = classifyMonotony;
exports.classifyDeltaPct = classifyDeltaPct;
exports.lbToKg = lbToKg;
exports.kgToLb = kgToLb;
// ─── ACWR (acute:chronic workload ratio, Gabbett) ────────────────────────────
/**
 * Zone di visualizzazione ACWR (decisione prodotto: mockup tab Sforzo
 * 2026-07-26, fascia ottimale 0.80–1.30 con sotto-banda di attenzione).
 * NB: la curva a campana del RECOVERY (0.80–1.05) è un'altra cosa — è
 * scoring interno del Recovery Index, non banding di visualizzazione.
 */
exports.ACWR_ZONES = {
    /** sotto: carico "non allenante" (detraining) */
    okMin: 0.8,
    /** fine zona verde */
    okMax: 1.2,
    /** fine zona ambra (fascia ottimale estesa mockup: 0.80–1.30) */
    warnMax: 1.3,
};
/** Finestre canoniche ACWR (rolling mean, come getRpe/effort). */
exports.ACWR_ACUTE_DAYS = 7;
exports.ACWR_CHRONIC_DAYS = 28;
/**
 * Giorni minimi di storico perché l'ACWR sia interpretabile: sotto soglia
 * il rapporto è un artefatto cold-start (tutto il carico negli ultimi 7
 * giorni ⇒ ACWR ≡ 28/7 = 4.0). Stessa soglia del fallback Strain recovery.
 */
exports.ACWR_MIN_HISTORY_DAYS = 21;
function classifyAcwr(acwr) {
    if (acwr >= exports.ACWR_ZONES.okMin && acwr <= exports.ACWR_ZONES.okMax)
        return 'ok';
    if (acwr > exports.ACWR_ZONES.okMax && acwr <= exports.ACWR_ZONES.warnMax)
        return 'warn';
    return 'alert';
}
/** Fuori dalla fascia ottimale estesa (0.80–1.30)? */
function acwrOutOfOptimalRange(acwr) {
    return acwr < exports.ACWR_ZONES.okMin || acwr > exports.ACWR_ZONES.warnMax;
}
/**
 * Banding ACWR a 5 zone usato dal readiness detail (StrainDetail.acwrZone):
 * confini 0.8 / 1.0 / 1.3 / 1.5. Più granulare di `classifyAcwr` (3 zone).
 */
exports.ACWR_BANDS = {
    dangerLowMax: 0.8,
    warningLowMax: 1.0,
    optimalMax: 1.3,
    warningHighMax: 1.5,
};
function classifyAcwr5(acwr) {
    if (acwr < exports.ACWR_BANDS.dangerLowMax)
        return 'danger_low';
    if (acwr < exports.ACWR_BANDS.warningLowMax)
        return 'warning_low';
    if (acwr <= exports.ACWR_BANDS.optimalMax)
        return 'optimal';
    if (acwr <= exports.ACWR_BANDS.warningHighMax)
        return 'warning_high';
    return 'danger_high';
}
/** Campana ACWR "ottimale" per recovery/antifragility: 0.80–1.05. */
exports.ACWR_BELL_OPTIMAL = { min: 0.8, max: 1.05 };
// ─── AFI (Antifragility Index, 0-100) ────────────────────────────────────────
/** Soglie AFI: ≥75 antifragile · ≥55 resilient · ≥35 fragile · <35 vulnerable. */
exports.AFI_THRESHOLDS = {
    antifragile: 75,
    resilient: 55,
    fragile: 35,
};
function classifyAFI(afi) {
    if (afi >= exports.AFI_THRESHOLDS.antifragile)
        return 'antifragile';
    if (afi >= exports.AFI_THRESHOLDS.resilient)
        return 'resilient';
    if (afi >= exports.AFI_THRESHOLDS.fragile)
        return 'fragile';
    return 'vulnerable';
}
/** AFI sotto cui, per N settimane consecutive, scatta l'alert antifragilità. */
exports.AFI_ALERT_SCORE = 30;
exports.AFI_ALERT_WEEKS = 3;
// ─── Soglie di dominio per-modulo (centralizzate qui, fonte unica) ───────────
/** Monotonia settimanale (Foster) sotto cui la distribuzione è "ottimale". */
exports.MONOTONY_OK_THRESHOLD = 1.5;
/** Blend del fattore muscolare readiness: CMJ vs mRSI. */
exports.RECOVERY_CMJ_MRSI_BLEND = { cmj: 0.6, mrsi: 0.4 };
/**
 * Copertura GPS (Catapult/STATSports): sotto `partialMin`% la seduta è
 * "Insufficient"; tra partialMin e fullMin è "Partial"; ≥fullMin è completa.
 */
exports.GPS_COVERAGE = { okMin: 90, goodMin: 80, partialMin: 70 };
/**
 * Soglia RI sotto cui l'alert settimanale del report diventa "critical"
 * (sopra resta "warning", entro la fascia < normale). Non coincide con le
 * soglie RI canoniche (è una scelta di severità del report): fonte unica qui.
 */
exports.REPORT_RI_CRITICAL = 40;
/** Pesi/soglie dello score di rischio infortunio (modulo injuries). */
exports.INJURY_RISK = {
    recurrenceWeight: 15,
    recurrenceMax: 40,
    freqWeight: 10,
    freqMax: 30,
    acwrHighScore: 20,
    acwrMidScore: 10,
    acwrMid: 1.1,
    highBand: 60,
    mediumBand: 30,
};
// ─── Copertura questionari (compilazione %) ──────────────────────────────────
/**
 * Bande di copertura compilazione questionari di squadra (Hooper/soggettivi):
 * ≥good verde · ≥warn giallo · sotto rosso. Prima ogni schermata aveva la sua
 * (70/40, 80/50, 70/80) e la stessa squadra cambiava colore fra viste.
 */
exports.QUESTIONNAIRE_COVERAGE_BANDS = { good: 80, warn: 50 };
function classifyCoverage(pct) {
    if (pct >= exports.QUESTIONNAIRE_COVERAGE_BANDS.good)
        return 'good';
    if (pct >= exports.QUESTIONNAIRE_COVERAGE_BANDS.warn)
        return 'warn';
    return 'low';
}
// ─── Percentili (fasce di prestazione) ───────────────────────────────────────
/** Fasce percentile per heatmap/prestazione: ≥high alto · ≥mid medio · ≥low basso. */
exports.PERCENTILE_BANDS = { high: 90, mid: 70, low: 30 };
function classifyPercentile(p) {
    if (p >= exports.PERCENTILE_BANDS.high)
        return 'high';
    if (p >= exports.PERCENTILE_BANDS.mid)
        return 'mid';
    if (p >= exports.PERCENTILE_BANDS.low)
        return 'low';
    return 'bottom';
}
// ─── Recovery Index (RI, 0-100) ──────────────────────────────────────────────
/**
 * Soglie canoniche del backend (readiness.service / interpolationRecovery):
 * 0-29 critical · 30-49 warning · 50-69 normal · 70-100 good.
 */
exports.RI_THRESHOLDS = {
    good: 70,
    normal: 50,
    warning: 30,
};
function classifyRI(ri) {
    if (ri >= exports.RI_THRESHOLDS.good)
        return 'good';
    if (ri >= exports.RI_THRESHOLDS.normal)
        return 'normal';
    if (ri >= exports.RI_THRESHOLDS.warning)
        return 'warning';
    return 'critical';
}
// ─── sRPE (AU) ───────────────────────────────────────────────────────────────
/**
 * Bin heatmap sRPE giornaliero in AU (mockup tab Sforzo):
 * ≤250 · 251-450 · 451-650 · 651-850 · >850.
 */
exports.SRPE_BINS = [250, 450, 650, 850];
/** Indice bin 0..4 per un valore sRPE (AU). */
function srpeBin(au) {
    if (au <= exports.SRPE_BINS[0])
        return 0;
    if (au <= exports.SRPE_BINS[1])
        return 1;
    if (au <= exports.SRPE_BINS[2])
        return 2;
    if (au <= exports.SRPE_BINS[3])
        return 3;
    return 4;
}
/** Vincoli RPE/minuti (allineati al DTO backend e agli input FE/mobile). */
exports.RPE_MIN = 1;
exports.RPE_MAX = 10;
exports.DURATION_MIN_MINUTES = 1;
exports.DURATION_MAX_MINUTES = 600;
// ─── Monotonia / Strain (Foster) ─────────────────────────────────────────────
/** Sopra questa soglia la monotonia settimanale è "di attenzione". */
exports.MONOTONY_WARN_THRESHOLD = 2.0;
function classifyMonotony(monotony) {
    return monotony > exports.MONOTONY_WARN_THRESHOLD ? 'warn' : 'ok';
}
// ─── Variazione settimanale (Δ%) ─────────────────────────────────────────────
exports.DELTA_WARN_PCT = 15;
exports.DELTA_ALERT_PCT = 50;
function classifyDeltaPct(deltaPct) {
    const abs = Math.abs(deltaPct);
    if (abs > exports.DELTA_ALERT_PCT)
        return 'alert';
    if (abs > exports.DELTA_WARN_PCT)
        return 'warn';
    return 'neutral';
}
// ─── Conversioni unità ───────────────────────────────────────────────────────
/**
 * UNICA costante lb→kg della piattaforma (il censimento ne ha trovate 3:
 * 0.45359, 0.453515, 0.453592 — tonnage e PR divergevano sullo stesso peso).
 */
exports.LB_TO_KG = 0.453592;
exports.KG_TO_LB = 1 / exports.LB_TO_KG;
exports.IN_TO_CM = 2.54;
exports.CM_TO_IN = 1 / exports.IN_TO_CM;
function lbToKg(lb) {
    return lb * exports.LB_TO_KG;
}
function kgToLb(kg) {
    return kg * exports.KG_TO_LB;
}
